import { useState, useEffect } from 'react';
import {
  fetchAllowlist,
  fetchAccessRequests,
  addToAllowlist,
  removeFromAllowlist,
  updateAccessRequest,
  AllowlistEntry,
  AccessRequest,
} from '../api/admin-access';

interface AdminBetaAccessProps {
  embedded?: boolean;
  correlationId?: string;
}

export function AdminBetaAccess({ embedded }: AdminBetaAccessProps) {
  const [allowlist, setAllowlist] = useState<AllowlistEntry[]>([]);
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'denied'>('pending');

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [allowlistData, requestsData] = await Promise.all([
        fetchAllowlist(),
        fetchAccessRequests(statusFilter === 'all' ? undefined : statusFilter),
      ]);
      setAllowlist(allowlistData);
      setRequests(requestsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const handleAddToAllowlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    try {
      await addToAllowlist(newEmail.trim(), newNotes.trim() || undefined);
      setNewEmail('');
      setNewNotes('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to allowlist');
    }
  };

  const handleRemove = async (email: string) => {
    if (!confirm(`Remove ${email} from allowlist?`)) return;

    try {
      await removeFromAllowlist(email);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await updateAccessRequest(id, 'approve');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    }
  };

  const handleDeny = async (id: string) => {
    try {
      await updateAccessRequest(id, 'deny');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? 'p-6' : 'max-w-7xl mx-auto p-6'}>
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-200 text-sm">
          {error}
        </div>
      )}

      {/* Add to Allowlist */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-3">Add to Allowlist</h2>
        <form onSubmit={handleAddToAllowlist} className="flex gap-2 flex-wrap">
          <input
            type="email"
            placeholder="Email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
          />
          <input
            type="text"
            placeholder="Notes (optional)"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
          >
            Add
          </button>
        </form>
      </div>

      {/* Allowlist Table */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-3">Allowlist ({allowlist.length})</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-2 px-3 text-slate-300 font-medium">Email</th>
                <th className="text-left py-2 px-3 text-slate-300 font-medium">Status</th>
                <th className="text-left py-2 px-3 text-slate-300 font-medium">Notes</th>
                <th className="text-left py-2 px-3 text-slate-300 font-medium">Created</th>
                <th className="text-right py-2 px-3 text-slate-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allowlist.map((entry) => (
                <tr key={entry.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                  <td className="py-2 px-3 text-white">{entry.email}</td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${entry.status === 'active' ? 'bg-green-900/30 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
                      {entry.status}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-slate-400 text-xs">{entry.notes || '—'}</td>
                  <td className="py-2 px-3 text-slate-400 text-xs">
                    {new Date(entry.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      onClick={() => handleRemove(entry.email)}
                      className="text-red-400 hover:text-red-300 text-xs font-medium"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Access Requests */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-white">Access Requests ({requests.length})</h2>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-1 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="all">All</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-2 px-3 text-slate-300 font-medium">Email</th>
                <th className="text-left py-2 px-3 text-slate-300 font-medium">Name</th>
                <th className="text-left py-2 px-3 text-slate-300 font-medium">Use Case</th>
                <th className="text-left py-2 px-3 text-slate-300 font-medium">Status</th>
                <th className="text-left py-2 px-3 text-slate-300 font-medium">Created</th>
                <th className="text-right py-2 px-3 text-slate-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                  <td className="py-2 px-3 text-white">{req.email}</td>
                  <td className="py-2 px-3 text-slate-300">{req.name}</td>
                  <td className="py-2 px-3 text-slate-400 text-xs">{req.useCase}</td>
                  <td className="py-2 px-3">
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        req.status === 'pending'
                          ? 'bg-yellow-900/30 text-yellow-400'
                          : req.status === 'approved'
                          ? 'bg-green-900/30 text-green-400'
                          : 'bg-red-900/30 text-red-400'
                      }`}
                    >
                      {req.status}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-slate-400 text-xs">
                    {new Date(req.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-2 px-3 text-right space-x-2">
                    {req.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(req.id)}
                          className="text-green-400 hover:text-green-300 text-xs font-medium"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleDeny(req.id)}
                          className="text-red-400 hover:text-red-300 text-xs font-medium"
                        >
                          Deny
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
